/*
 * spd-a11y.js - keep the keyboard out of aria-hidden content.
 *
 * Flickity cells, the cart drawer and the PhotoSwipe gallery all mark subtrees
 * aria-hidden but leave the links and buttons inside them tabbable, so keyboard
 * users land on content screen readers cannot see. WCAG 2.0 A / 2.2 A.
 *
 * Two cases, and they need different answers:
 *   offscreen containers (drawer, lightbox, pixel sandbox)
 *       -> inert, so nothing reaches them at all
 *   carousel cells that are still on screen and clickable
 *       -> tabindex="-1" only; inert would kill the mouse too, and 15 visible
 *          product cards on the homepage sit in this case
 *
 * Everything is recorded on a data attribute and put back when aria-hidden
 * clears, so the components that own these subtrees keep control of them.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button,input,select,textarea,iframe,summary,[tabindex]:not([tabindex="-1"])';
  var supportsInert = 'inert' in HTMLElement.prototype;

  // Recent focus history, newest first. When a panel closes we need somewhere to
  // send the caret, and the right answer is whatever the user was on before they
  // went in - the cart button, the gallery thumbnail, the quick-shop trigger.
  var focusTrail = [];

  document.addEventListener('focusin', function (e) {
    if (e.target === focusTrail[0]) return;
    focusTrail.unshift(e.target);
    if (focusTrail.length > 6) focusTrail.length = 6;
  }, true);

  function returnFocus(from) {
    for (var i = 0; i < focusTrail.length; i++) {
      var c = focusTrail[i];
      if (!c || c === from || from.contains(c) || !c.isConnected) continue;
      if (c.closest('[aria-hidden="true"],[inert]')) continue;
      var r = c.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      c.focus();
      if (document.activeElement === c) return true;
    }
    return false;
  }

  function perceivable(el) {
    var s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') return false;

    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;

    // Parked offscreen (the cart drawer keeps its Hulk forms at left:-99999px).
    // Measured against the document, not the viewport, so content that is
    // merely scrolled out of sight still counts as perceivable.
    if (r.right + window.pageXOffset <= 0) return false;
    if (r.bottom + window.pageYOffset <= 0) return false;

    return true;
  }

  function mute(el) {
    // Something that just went hidden must not keep the caret, or the drawer
    // never goes inert again because it still holds focus. Hand the caret back
    // to whatever opened this; dropping it on <body> would satisfy the audit but
    // leave a keyboard user tabbing from the top of the page again.
    var active = document.activeElement;
    if (active && active !== document.body && el.contains(active)) {
      if (!returnFocus(el)) active.blur();
    }

    if (!supportsInert || perceivable(el)) {
      var nodes = el.querySelectorAll(FOCUSABLE);
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n.hasAttribute('data-spd-tabindex')) continue;
        n.setAttribute('data-spd-tabindex', n.hasAttribute('tabindex') ? n.getAttribute('tabindex') : '');
        n.setAttribute('tabindex', '-1');
      }
    } else if (!el.inert) {
      el.inert = true;
      el.setAttribute('data-spd-inert', '');
    }
  }

  function restore(el) {
    if (el.hasAttribute('data-spd-inert')) {
      el.inert = false;
      el.removeAttribute('data-spd-inert');
    }
    var nodes = el.querySelectorAll('[data-spd-tabindex]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var prev = n.getAttribute('data-spd-tabindex');
      if (prev === '') {
        n.removeAttribute('tabindex');
      } else {
        n.setAttribute('tabindex', prev);
      }
      n.removeAttribute('data-spd-tabindex');
    }
  }

  function sync(el) {
    if (!el || el.nodeType !== 1) return;
    if (el.getAttribute('aria-hidden') === 'true') {
      if (el.querySelector(FOCUSABLE) || el.querySelector('[data-spd-tabindex]')) mute(el);
    } else {
      restore(el);
    }
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.hasAttribute('aria-hidden')) sync(root);
    var nodes = root.querySelectorAll('[aria-hidden]');
    for (var i = 0; i < nodes.length; i++) sync(nodes[i]);
  }

  function start() {
    scan(document.body);

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'attributes') {
          sync(r.target);
        } else {
          for (var j = 0; j < r.addedNodes.length; j++) {
            var node = r.addedNodes[j];
            scan(node);
            // Apps inject into containers that are already aria-hidden - the
            // Hulk forms iframes arrive this way, long after the first pass.
            if (node.nodeType === 1) {
              var host = node.parentElement && node.parentElement.closest('[aria-hidden="true"]');
              if (host) sync(host);
            }
          }
        }
      }
    }).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

(function () {
  // Hulk renders its forms in untitled iframes. Give each one a name.
  function titleFrames() {
    document.querySelectorAll('iframe[src*="hulkapps"]:not([title])').forEach(function (f) {
      f.setAttribute('title', 'Request form');
    });
  }
  // The theme opens submenus on hover and on Enter. Add Space and Arrow Down,
  // and close the menu when focus leaves it.
  function menuKeys() {
    document.querySelectorAll('[data-navmenu-parent]').forEach(function (li) {
      var link = li.querySelector('a.navmenu-link-parent');
      if (!link) return;
      link.addEventListener('keydown', function (e) {
        if (e.key !== ' ' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        if (!li.classList.contains('open')) link.click();
        if (e.key === 'ArrowDown') {
          var first = li.querySelector('[data-navmenu-submenu] a');
          if (first) setTimeout(function () { first.focus(); }, 60);
        }
      });
      li.addEventListener('focusout', function (e) {
        if (!li.contains(e.relatedTarget)) li.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
      });
      // Escape anywhere in an open submenu closes it and puts focus back on the parent link.
      li.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !li.classList.contains('open')) return;
        e.preventDefault();
        link.focus();
        li.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
      });
    });
  }
  function run() {
    titleFrames();
    menuKeys();
    new MutationObserver(titleFrames).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

(function () {
  // Privacy notice under every form that collects details, wherever the form comes from.
  var spdNoticeText = 'We use the details you enter here to respond to your request. See our <a href="/pages/privacy-policy">Privacy Policy</a> for what we collect and why.';
  function notice() { var p = document.createElement('p'); p.className = 'spd-notice'; p.innerHTML = spdNoticeText; return p; }
  function addNotices() {
    document.querySelectorAll('form[action*="/contact"]').forEach(function (f) {
      if (f.closest('.spd-footer') || f.querySelector('.spd-notice')) return;
      var btn = f.querySelector('button[type="submit"], input[type="submit"]');
      if (btn) btn.parentNode.insertBefore(notice(), btn);
    });
    document.querySelectorAll('iframe[src*="hulkapps"]').forEach(function (fr) {
      if (fr.closest('.ccd__request-panel, .ccd__hulk-forms')) return;
      var host = fr.closest('.pxFormGenerator') || fr.parentElement;
      if (!host || (host.parentElement && host.parentElement.querySelector('.spd-notice'))) return;
      host.insertAdjacentElement('afterend', notice());
    });
  }
  function run() {
    if (!document.getElementById('spd-notice-style')) {
      var st = document.createElement('style'); st.id = 'spd-notice-style';
      st.textContent = '.spd-notice{margin:10px 0 0;font-size:12px;line-height:1.5;color:#4c5764}.spd-notice a{color:inherit;text-decoration:underline}';
      document.head.appendChild(st);
    }
    addNotices();
    new MutationObserver(addNotices).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

(function () {
  // Carousel controls sit before the slides in the tab order, so a keyboard user can
  // operate a carousel without tabbing through every card first. Flickity appends its
  // buttons after the viewport. They are absolutely positioned, so the move is invisible.
  function orderControls(car) {
    var viewport = null, controls = [];
    for (var c = car.firstElementChild; c; c = c.nextElementSibling) {
      if (c.classList.contains('flickity-viewport')) viewport = c;
      else if (c.classList.contains('flickity-button') || c.classList.contains('flickity-page-dots')) controls.push(c);
    }
    // The arrows are the control now, so the container itself need not take focus.
    if (car.getAttribute('tabindex') === '0') car.setAttribute('tabindex', '-1');
    if (!viewport) return;
    for (var i = 0; i < controls.length; i++) {
      if (controls[i].compareDocumentPosition(viewport) & Node.DOCUMENT_POSITION_FOLLOWING) continue;
      car.insertBefore(controls[i], viewport);
    }
  }
  function run() {
    document.querySelectorAll('.flickity-enabled').forEach(orderControls);
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        // A carousel that initialises after load gains the class, with or without buttons.
        if (records[i].type === 'attributes') {
          if (records[i].target.classList.contains('flickity-enabled')) orderControls(records[i].target);
          continue;
        }
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.classList.contains('flickity-button') || n.classList.contains('flickity-page-dots')) orderControls(n.parentElement);
          else if (n.querySelector && n.querySelector('.flickity-enabled')) n.querySelectorAll('.flickity-enabled').forEach(orderControls);
        }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

(function () {
  // Shopper Approved injects markup the theme cannot edit. Patch the 4 things the checker
  // flags: labels that drop the visible text, labelled <option>s, star groups and notices
  // that take focus without being controls, and reviewer names as level-4 headings.
  function patch(root) {
    if (!root.querySelectorAll) return;
    // The visible review count is also the voice-control name for this in-page link.
    root.querySelectorAll('a.sa_jump_to_reviews[aria-label]').forEach(function (a) {
      if ((a.textContent || '').trim()) a.removeAttribute('aria-label');
    });
    root.querySelectorAll('a[aria-label][href*="shopperapproved.com"], a.sa-verify-reviews[aria-label]').forEach(function (a) {
      // The label replaced the visible text (and the logo's alt). Keep the visible name and
      // add the rest as hidden text, so the name a screen reader hears starts with what is shown.
      var label = a.getAttribute('aria-label') || '';
      var visible = [].map.call(a.querySelectorAll('img[alt]'), function (i) { return i.alt; }).concat((a.textContent || '').replace(/\s+/g, ' ').trim()).join(' ').trim();
      if (!label || a.querySelector('.spd-sr-only')) return;
      var extra = label.replace(visible, '').replace(/^[\s,()]+|[\s,]+$/g, '');
      a.removeAttribute('aria-label');
      if (extra) {
        var hint = document.createElement('span');
        hint.className = 'spd-sr-only';
        hint.setAttribute('style', 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap');
        hint.textContent = ', ' + extra;
        a.appendChild(hint);
      }
    });
    root.querySelectorAll('option[aria-label][href*="shopperapproved"]').forEach(function (o) { o.removeAttribute('aria-label'); });
    root.querySelectorAll('[id^="sa_grp_"][tabindex], .group-stars[tabindex]').forEach(function (el) {
      el.removeAttribute('tabindex');
      if (!el.getAttribute('role')) el.setAttribute('role', 'img');
    });
    root.querySelectorAll('[id^="sa_"] p[tabindex], [id^="SA_"] p[tabindex], .sa-body p[tabindex], [class*="shopperapproved"] p[tabindex]').forEach(function (p) { p.removeAttribute('tabindex'); });
    root.querySelectorAll('.SA__customer_name[role="heading"]').forEach(function (h) { h.setAttribute('aria-level', '3'); });
    // The widget page's footer link holds only a logo with an empty alt. Hidden text rather than
    // aria-label, because the rule above would otherwise rewrite the label on its next pass.
    root.querySelectorAll('a.sa_footer').forEach(function (a) {
      if ((a.textContent || '').trim() || a.querySelector('img[alt]:not([alt=""])') || a.querySelector('.spd-sr-only')) return;
      var hint = document.createElement('span');
      hint.className = 'spd-sr-only';
      hint.setAttribute('style', 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap');
      hint.textContent = 'Read our reviews on Shopper Approved';
      a.appendChild(hint);
    });
  }
  function run() {
    patch(document);
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; patch(document); }, 200);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

(function () {
  // HTML pasted into the page and article editors repeats the same handful of gaps on every
  // page it was pasted into. Patch them once here instead of re-editing each page in admin.
  var SHARE = { facebook: 'Facebook', x: 'X', twitter: 'X', linkedin: 'LinkedIn', pinterest: 'Pinterest', email: 'email' };
  function debounce(fn, ms) { var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); }; }
  function main() { return document.querySelector('main') || document.getElementById('site-main'); }
  function landmarks() {
    // The floating quote CTA's own inline script moves it to <body>, outside every landmark.
    // position: fixed still works inside <main>, which has no transform.
    var m = main();
    if (!m) return;
    document.querySelectorAll('body > #spd-float-cta, body > a.spd-float').forEach(function (cta) { m.appendChild(cta); });
  }
  function names() {
    // "Expand All" with an aria-label that does not contain those words fails voice control.
    document.querySelectorAll('.spd-expand-toggle[aria-label]').forEach(function (b) {
      var text = (b.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (text && b.getAttribute('aria-label').toLowerCase().indexOf(text) === -1) b.removeAttribute('aria-label');
    });
    document.querySelectorAll('.spd-article-share__icon:not([aria-label])').forEach(function (a) {
      if ((a.textContent || '').trim() || a.querySelector('img[alt]:not([alt=""])')) return;
      var net = (a.className.match(/spd-share-([a-z]+)/) || [])[1];
      a.setAttribute('aria-label', 'Share on ' + (SHARE[net] || net || 'social media'));
    });
    // Includes Shopify's hidden pixel sandboxes: checkers count them too, and want each title unique.
    var pixels = document.querySelectorAll('iframe[title^="Analytics"]').length;
    document.querySelectorAll('iframe:not([title])').forEach(function (f) {
      var src = f.getAttribute('src') || '';
      f.setAttribute('title', /youtube|youtu\.be/.test(src) ? 'YouTube video' : /vimeo/.test(src) ? 'Vimeo video' : /web-pixel/.test(src + f.id) ? 'Analytics ' + (++pixels) : 'Embedded content');
    });
    newTabHints();
    // Shopper Approved's survey and consent tooltip use an h4 for a sentence, which skips a level.
    document.querySelectorAll('.sa-survey-footer h4, .herebyConsent h4, #herebyTooltip_wrapper h4').forEach(function (h) {
      var p = document.createElement('p');
      p.className = (h.className ? h.className + ' ' : '') + 'spd-was-h4';
      p.setAttribute('style', 'font-weight:700;margin:0 0 6px');
      while (h.firstChild) p.appendChild(h.firstChild);
      h.parentNode.replaceChild(p, h);
    });
    document.querySelectorAll('#productInfoImage[alt="Product Image"]').forEach(function (img) { img.setAttribute('alt', ''); });
    // Warranty tables carry blank spacer rows: an empty header cell reads as a header with no name.
    document.querySelectorAll('.metafield-warranty-table tr').forEach(function (tr) {
      if (!tr.textContent.trim() && !tr.querySelector('img, input, select, a')) tr.remove();
    });
    // An empty alt already means decorative. aria-hidden says it the way checkers expect.
    document.querySelectorAll('img[alt=""]:not([aria-hidden])').forEach(function (img) { img.setAttribute('aria-hidden', 'true'); });
    document.querySelectorAll('[id=""]').forEach(function (e) { e.removeAttribute('id'); });
    // Shopify's wallet placeholders are empty divs with role=button while the real buttons load.
    document.querySelectorAll('.wallet-cart-button__skeleton[role="button"], .wallet-cart-button[aria-hidden="true"][role="button"]').forEach(function (el) { el.removeAttribute('role'); });
    // Several product forms on one page repeat Shopify's payment button ids. Keep the first, number the rest.
    ['more-payment-options-link', 'shop_pay_button_div'].forEach(function (id) {
      var all = document.querySelectorAll('[id="' + id + '"]');
      for (var i = 1; i < all.length; i++) all[i].id = id + '-' + (i + 1);
    });
  }
  function newTabHints() {
    // Links that open a new tab say so, for the people who cannot see the tab bar change.
    document.querySelectorAll('a[target="_blank"]:not([data-spd-newtab])').forEach(function (a) {
      a.setAttribute('data-spd-newtab', '');
      var label = (a.getAttribute('aria-label') || a.textContent || '').trim();
      if (/new (tab|window)|opens in/i.test(label + ' ' + (a.getAttribute('title') || ''))) return;
      if (a.hasAttribute('aria-label')) { a.setAttribute('aria-label', label + ' (opens in a new tab)'); return; }
      var hint = document.createElement('span');
      hint.className = 'spd-sr-only';
      hint.setAttribute('style', 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap');
      hint.textContent = ' (opens in a new tab)';
      a.appendChild(hint);
    });
  }
  function reach() {
    // A table wider than the page scrolls sideways, so the keyboard needs a way to reach it.
    var m = main();
    if (!m) return;
    m.querySelectorAll('table').forEach(function (t) {
      var o = getComputedStyle(t).overflowX;
      var scrolls = (o === 'auto' || o === 'scroll') && t.scrollWidth > t.clientWidth + 1;
      if (scrolls && !t.hasAttribute('tabindex')) { t.setAttribute('tabindex', '0'); t.setAttribute('data-spd-scroll', ''); }
      else if (!scrolls && t.hasAttribute('data-spd-scroll')) { t.removeAttribute('tabindex'); t.removeAttribute('data-spd-scroll'); }
    });
  }
  function run() {
    landmarks(); names(); reach();
    // Watch the whole body: the cart drawer, footer forms and wallet buttons render outside <main>.
    new MutationObserver(debounce(function () { names(); reach(); }, 200)).observe(document.body, { childList: true, subtree: true });
    // Not debounced: Shopify adds the hCaptcha Privacy and Terms links the moment a form is focused.
    new MutationObserver(newTabHints).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', debounce(reach, 200));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

(function () {
  // A carousel arrow that disables itself at the end (Flickity, the product row) drops the
  // keyboard user's focus to <body>. Hand it to the other arrow, else to the carousel itself.
  var ARROWS = '.flickity-button, .custom-auto-products__arrow, .trusted-organizations__arrow';
  var last = null;
  document.addEventListener('focusin', function (e) { last = e.target; }, true);
  function rehome(btn) {
    // Chrome drops focus from a disabled button on the next frame, so at this point the
    // button may still read as active. Either way, if it was the last thing focused, move on.
    var a = document.activeElement;
    if (btn !== last || (a !== btn && a !== document.body)) return;
    var group = btn.parentElement;
    var other = group && [].filter.call(group.querySelectorAll(ARROWS), function (b) { return b !== btn && !b.disabled; })[0];
    if (other) { other.focus(); return; }
    var car = btn.closest('.flickity-enabled, .custom-auto-products, .trusted-organizations, .shopify-section');
    if (!car) return;
    if (!car.hasAttribute('tabindex')) car.setAttribute('tabindex', '-1');
    car.focus();
  }
  function run() {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (t.disabled && t.matches && t.matches(ARROWS)) rehome(t);
      }
    }).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['disabled'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

(function () {
  // UserWay's monitor wants every link to one destination to carry one name. The visible
  // wording stays as written (a nav label, a footer label, a call to action), and each link in
  // a group gets the same aria-label built from those words, so every name still contains what
  // its own link shows (WCAG 2.5.3 Label in Name). Groups whose members already agree, links to
  // the home page, and links whose text is a paragraph (cards) are left alone.
  var MAX = 90;
  function norm(s) { return (s || '').replace(/\s+/g, ' ').trim(); }
  function fold(s) {
    return norm(s).toLowerCase().replace(/[‘-‟…‹›→»]/g, '').replace(/[^\w\sÀ-ɏ]/g, '').replace(/\s+/g, ' ').trim();
  }
  function text(el, skipHidden) {
    // Text the way the scanner reads it (skipHidden false) or the way a sighted visitor sees it.
    var out = '', w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT), n;
    while ((n = w.nextNode())) {
      var p = n.parentNode, ok = true;
      while (p && p !== el) {
        var tag = p.nodeName.toLowerCase();
        if (tag === 'noscript' || tag === 'script' || tag === 'style' || tag === 'template') { ok = false; break; }
        if (skipHidden && (tag === 'svg' || (p.getAttribute && p.getAttribute('aria-hidden') === 'true'))) { ok = false; break; }
        p = p.parentNode;
      }
      if (ok) out += n.nodeValue + ' ';
    }
    return norm(out);
  }
  function scannerName(a) {
    return fold(a.getAttribute('aria-label') || text(a, false) || a.getAttribute('title'));
  }
  function key(a) {
    var href = a.getAttribute('href');
    if (!href || /^(#|javascript:|mailto:|tel:)/i.test(href)) return null;
    var u; try { u = new URL(href, location.href); } catch (e) { return null; }
    if (u.origin !== location.origin) return null;
    var path = u.pathname.replace(/\/$/, '');
    if (!path) return null;
    return path + u.search;
  }
  function run() {
    var groups = {}, order = [];
    document.querySelectorAll('a[href]').forEach(function (a) {
      if (a.closest('[aria-hidden="true"], noscript')) return;
      var k = key(a); if (!k) return;
      if (!groups[k]) { groups[k] = []; order.push(k); }
      groups[k].push(a);
    });
    order.forEach(function (k) {
      var links = groups[k];
      if (links.length < 2) return;
      var names = {}; links.forEach(function (a) { names[scannerName(a)] = 1; });
      if (Object.keys(names).length < 2 && !links.some(function (a) { return a.hasAttribute('data-spd-same-target'); })) return;
      if (links.some(function (a) { return a.target === '_blank'; })) return;
      var members = links.map(function (a) { return { a: a, shown: text(a, true) }; });
      if (members.some(function (m) { return m.shown.length > MAX; })) return;
      var cands = [];
      members.forEach(function (m) {
        var t = m.shown || norm(m.a.getAttribute('data-spd-same-target-text') || '');
        if (!t) return;
        var f = fold(t); if (!f) return;
        if (!cands.some(function (c) { return c.f === f; })) cands.push({ t: t, f: f });
      });
      if (!cands.length) {
        var fallback = links.map(function (a) { return norm(a.getAttribute('aria-label')); }).filter(Boolean)[0];
        if (!fallback) return;
        cands.push({ t: fallback, f: fold(fallback) });
      }
      // Drop a wording that another wording already contains, keep document order.
      var kept = cands.filter(function (c) {
        return !cands.some(function (o) { return o !== c && o.f !== c.f && o.f.indexOf(c.f) !== -1; });
      });
      var label = kept.map(function (c) { return c.t; }).join(', ');
      links.forEach(function (a) {
        a.setAttribute('data-spd-same-target', '');
        if (scannerName(a) !== fold(label)) a.setAttribute('aria-label', label);
      });
    });
  }
  var t;
  function later() { clearTimeout(t); t = setTimeout(run, 250); }
  function start() {
    run();
    new MutationObserver(later).observe(document.body, { childList: true, subtree: true, characterData: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
